/**
 * `useAgentTurn` — THE SINGLE TRANSPORT SEAM OF THE AGENT SURFACE.
 *
 * ══ NO OTHER FILE MAY KNOW HOW A TURN TRAVELS ══════════════════════════════
 *
 * 🔴 Phase A is POST-then-long-poll; Phase B is response streaming (spec 14.8,
 * already priced and risk-assessed). The whole point of concentrating it here
 * is that the switch is ONE file: no page, no component and no other hook
 * contains a poll interval, a `turn_id`, a `/v1/admin/agent/turn` path or a
 * `status === 'done'` branch. `tests/unit/lint/agent-screen-single-view.test.ts`
 * asserts that literally, so a component that "just polls quickly for one
 * thing" fails the build rather than quietly forking the transport.
 *
 * ══ WHY NOT ONE REQUEST PER SECOND ═════════════════════════════════════════
 *
 * Spec 14.2 measured it: PROD's WAF `RateLimitRule` is Limit 1000 / Action
 * Block over five minutes for the ENTIRE API, i.e. 200 req/min per IP. A 1 Hz
 * short poll costs ~31 requests per question, so five admins asking one
 * question a minute reach 775 of 1,000 and the sixth admin BLOCKS THE OFFICE IP
 * FOR EVERY ROUTE. The 20-second server-side hold costs ~4. The fix is the
 * transport, never a looser WAF rule.
 *
 * ══ A HIDDEN TAB ISSUES ZERO REQUESTS ══════════════════════════════════════
 *
 * Polling parks while `document.hidden` and resumes with ONE IMMEDIATE poll on
 * visibility. That is strictly better than any interval choice: a tab left open
 * over a weekend costs nothing, and the operator who comes back sees the answer
 * without waiting out a ladder step.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiUrl } from '@refinedev/core';

import { adminFetch, AdminApiError } from '@/providers/admin-api';
import type {
  TurnAcceptedResponse,
  TurnPollResponse,
} from '@/pages/agent/protocol';
import type {
  AgentScreenState,
  TransportFailure,
} from '@/pages/agent/turn-state';
import {
  POLL_GIVE_UP_MS,
  deriveScreenState,
  nextPollDelayMs,
} from '@/pages/agent/turn-state';

/** The one route prefix. Nothing outside this file may spell it. */
const TURN_PATH = '/v1/admin/agent/turn';

/** `session_id` must match the front door's `^[A-Za-z0-9_-]{1,128}$`. */
function newSessionId(): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `chat-${uuid}`;
}

function failureOf(error: unknown): TransportFailure {
  if (error instanceof AdminApiError) {
    const body = error.body as { error?: unknown } | undefined;
    const code = typeof body?.error === 'string' ? body.error : `http_${error.status}`;
    return { status: error.status, code };
  }
  return { status: null, code: 'network_error' };
}

interface ActiveTurn {
  turnId: string;
  askedAt: number;
  question: string;
}

/** One finished exchange, kept for the operator's eyes only. */
export interface CompletedExchange {
  readonly question: string;
  /** Already terminal, so it can never change again. */
  readonly state: AgentScreenState;
}

/** The kinds that end a turn. A turn in any other kind is still moving. */
const TERMINAL_KINDS: ReadonlySet<AgentScreenState['kind']> = new Set([
  'answered',
  'declined',
  'refused',
  'error',
  'stopped',
]);

export interface AgentTurnController {
  /** The ONLY thing the page renders from. */
  state: AgentScreenState;
  /**
   * The exchanges finished EARLIER IN THIS SESSION, oldest first.
   *
   * 🔴 CLIENT-SIDE ONLY, AND THAT IS THE DESIGN, NOT A SHORTCUT. The transport
   * is two fields forever — `run-agent-turn.ts:104`: "the client CANNOT post a
   * transcript, because there is no parameter to put one in" — because a client
   * that could post a transcript could inject text past the input gate that
   * screens the single `question` field. Nothing here is ever sent.
   *
   * So the MODEL stays stateless and the OPERATOR gets their record. Before
   * this, `ask()` destroyed the previous answer at DISPATCH — not replaced 15
   * seconds later, gone immediately — with no undo and no scrollback, while the
   * screen advertised `role="log"`, a scroll container and a chat bubble.
   * Comparing two numbers, the commonest analytics act, was impossible.
   */
  history: readonly CompletedExchange[];
  /** The raw poll body — the environment / egress pills read it while RUNNING. */
  poll: TurnPollResponse | null;
  turnId: string | null;
  askedQuestion: string | null;
  /**
   * True from the moment `ask()` is called until the POST settles. Distinct
   * from `busy`, which means "a turn exists and is moving" and drives the poll
   * effects; widening `busy` would have started polling for a turn id that
   * does not exist yet. The composer ORs the two to disable itself.
   */
  sending: boolean;
  sessionId: string;
  busy: boolean;
  ask: (question: string) => void;
  stop: () => void;
  reset: () => void;
}

export function useAgentTurn(): AgentTurnController {
  const apiUrl = useApiUrl();
  const [sessionId, setSessionId] = useState<string>(newSessionId);
  const [turn, setTurn] = useState<ActiveTurn | null>(null);
  /**
   * 🔴 THE QUESTION IN FLIGHT, BEFORE A TURN EXISTS.
   *
   * `ask()` opens with `setTurn(null)` and only sets a turn once the POST
   * resolves. Everything the page gates on — `busy`, `askedAt`,
   * `askedQuestion`, and through them `conversing` in index.tsx — derived from
   * `turn`, so for the whole round trip (~300-600 ms, measured on DEV) all of
   * them read as "nothing happening". The transcript unmounted, the hero
   * screen and starter chips came back, and the composer was already empty.
   * Every follow-up did this.
   *
   * The `dispatching` ref below fixes the OTHER half of that window — the
   * double dispatch — and a ref is right for that, because it must hold within
   * one tick. But a ref cannot re-render, so it cannot keep the screen mounted.
   * This is STATE for that reason: set synchronously at dispatch, cleared in
   * the POST's `finally`, and batched with `setTurn` on the resolve path so
   * there is no frame where both are null.
   */
  const [pending, setPending] = useState<ActiveTurn | null>(null);
  const [poll, setPoll] = useState<TurnPollResponse | null>(null);
  const [transportError, setTransportError] = useState<TransportFailure | null>(null);
  const [stopped, setStopped] = useState(false);
  const [clock, setClock] = useState<number>(() => Date.now());

  // A monotonic guard: a poll response for a turn the operator has already
  // replaced must never overwrite the new turn's state. Without it, a slow
  // 20-second hold from the previous question lands on top of the current one.
  const activeTurnId = useRef<string | null>(null);
  const [history, setHistory] = useState<CompletedExchange[]>([]);
  /**
   * The exchange currently on screen. A REF because `ask` must read it without
   * taking `state` as a dependency — `state` changes on every poll tick, and a
   * dependency would rebuild `ask` (and therefore every `onClick` bound to it)
   * several times a second.
   */
  const onScreen = useRef<CompletedExchange | null>(null);

  /**
   * 🔴 ONE QUESTION, ONE MODEL LOOP — the CLIENT half of that invariant.
   *
   * `busy` cannot do this job. It derives from `turn !== null` (below), and
   * `ask()` begins by setting `turn` to null — so for the whole POST round
   * trip, ~300-600ms, `busy` is FALSE. The composer's `if (busy) return` sees
   * nothing to block, and on the empty screen the starter chips have not
   * unmounted yet either, because `state.kind` is still `empty`.
   *
   * MEASURED ON DEV, 2026-08-27. Two turns for one question, 0.1s apart:
   *
   *   11:50:36  answered  9641ms  ← started 11:50:26.4
   *   11:50:33  answered  6738ms  ← started 11:50:26.3
   *
   * Two dispatches in the front-door log, two DISTINCT turn ids, so it was two
   * POSTs from the browser rather than a Lambda retry. That is two model calls,
   * two `admin_audit_log` rows and two `agent_turn_log` rows for ONE operator
   * question — and `admin-agent-async-retry-pinned` says plainly why that
   * matters: "a record that multiplies its subject is not a record".
   *
   * A ref, not state: the guard has to hold WITHIN one tick, before React has
   * re-rendered anything. Cleared in `finally` so a failed POST does not wedge
   * the screen.
   */
  const dispatching = useRef(false);

  const terminal =
    poll !== null && (poll.status === 'done' || poll.status === 'failed');
  const gaveUp = turn !== null && clock - turn.askedAt > POLL_GIVE_UP_MS;
  const busy = turn !== null && !terminal && !stopped && !gaveUp && transportError === null;

  // ── THE TICK. Only while a turn is in flight, and only to age the elapsed
  //    counter that drives the slow-turn hint. It touches no network.
  useEffect(() => {
    if (!busy) return;
    const id = setInterval(() => setClock(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [busy]);

  // ── THE POLL LOOP.
  useEffect(() => {
    // 🔴 `gaveUp` is NOT in this guard (2026-09-15). A hidden tab parks the
    // loop, but the one-second tick keeps ageing `clock`, so a hidden tab
    // crossed the give-up deadline without asking the server once — and this
    // guard then tore down the visibility listener too, so returning to the
    // tab could never collect a turn the worker had finished in 14 s (PROD,
    // turn ae189443). Past the deadline the loop stops SCHEDULING polls, but
    // the listener stays: one poll on return, and a terminal server state
    // outranks the clock (agent-turn-state-giveup.test.ts clause 4). Pinned by
    // tests/unit/admin/agent-hidden-tab-collects-the-answer.test.ts.
    if (turn === null || stopped || terminal || transportError !== null) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const runPoll = async (): Promise<void> => {
      if (cancelled) return;
      // PARKED, not throttled: a hidden tab issues no request at all. The
      // visibility listener below restarts the loop with one immediate poll.
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const response = await adminFetch(`${apiUrl}${TURN_PATH}/${turn.turnId}`);
        const body = (await response.json()) as TurnPollResponse;
        if (cancelled || activeTurnId.current !== turn.turnId) return;
        setPoll(body);
        setClock(Date.now());
      } catch (error) {
        if (cancelled || activeTurnId.current !== turn.turnId) return;
        setTransportError(failureOf(error));
      }
    };

    // The FIRST poll goes out immediately: the front door answers
    // `poll_after_ms: 0`, and the server hold is what paces the loop. Past the
    // give-up deadline nothing is scheduled — only a return to the tab polls.
    if (!gaveUp) {
      const delay = poll === null ? 0 : nextPollDelayMs(Date.now() - turn.askedAt);
      timer = setTimeout(() => void runPoll(), delay);
    }

    const onVisibility = (): void => {
      if (typeof document !== 'undefined' && !document.hidden) void runPoll();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [apiUrl, turn, poll, stopped, terminal, gaveUp, transportError]);

  const ask = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (trimmed.length === 0) return;
      // Re-entry during the POST window. See `dispatching` above.
      if (dispatching.current) return;
      dispatching.current = true;

      // Keep what is leaving the screen, IF it finished. A turn abandoned
      // mid-flight is not a record of anything and is dropped.
      const outgoing = onScreen.current;
      if (outgoing !== null && TERMINAL_KINDS.has(outgoing.state.kind)) {
        setHistory((prior) => [...prior, outgoing]);
      }
      onScreen.current = null;

      setPoll(null);
      setTransportError(null);
      setStopped(false);
      setTurn(null);
      activeTurnId.current = null;

      // Stamped HERE, not inside the async body, so `pending` and the eventual
      // `turn` carry the same `askedAt` and the give-up clock never restarts.
      const askedAt = Date.now();
      setPending({ turnId: '', askedAt, question: trimmed });

      void (async () => {
        try {
          const response = await adminFetch(`${apiUrl}${TURN_PATH}`, {
            method: 'POST',
            body: JSON.stringify({ session_id: sessionId, question: trimmed }),
          });
          const accepted = (await response.json()) as TurnAcceptedResponse;
          activeTurnId.current = accepted.turn_id;
          setTurn({ turnId: accepted.turn_id, askedAt, question: trimmed });
          setClock(Date.now());
        } catch (error) {
          // A POST that never produced a turn id still has to reach the screen
          // as a NAMED failure — `deriveScreenState` needs `askedAt` to be set
          // for anything but the empty state, so the turn is recorded with the
          // id the server never gave us and the transport error outranks it.
          activeTurnId.current = null;
          setTurn({ turnId: '', askedAt, question: trimmed });
          setTransportError(failureOf(error));
        } finally {
          // Released once the POST has settled either way. From here `busy` is
          // true (or the error panel is up), so the existing guards take over.
          // `setPending(null)` lands in the same continuation as the `setTurn`
          // above, so React batches them: the screen goes pending -> turn with
          // no empty frame in between.
          setPending(null);
          dispatching.current = false;
        }
      })();
    },
    [apiUrl, sessionId],
  );

  const stop = useCallback(() => {
    // Honest about what a client abort can guarantee: the worker keeps running.
    activeTurnId.current = null;
    setStopped(true);
  }, []);

  const reset = useCallback(() => {
    activeTurnId.current = null;
    setTurn(null);
    setPending(null);
    setPoll(null);
    setTransportError(null);
    setStopped(false);
    // "New question" means a clean screen, so the session record goes with it.
    // The alternative — keeping history across a reset — would make the button
    // mean nothing an operator could predict.
    onScreen.current = null;
    setHistory([]);
    // 🔴 AND A NEW SERVER CONVERSATION (2026-09-15). The server keys its session
    // memory by `session_id` and replays up to six earlier exchanges into each
    // turn. This id used to be minted once per mount with no setter, so the
    // button cleared the screen while the server kept the old conversation:
    // all fourteen questions of the PROD battery ran under one session id.
    // Pinned by tests/unit/admin/agent-new-question-starts-a-new-conversation.test.ts.
    setSessionId(newSessionId());
  }, []);

  const state = useMemo<AgentScreenState>(
    () =>
      deriveScreenState({
        poll,
        transportError,
        stopped,
        askedAt: turn?.askedAt ?? pending?.askedAt ?? null,
        now: clock,
      }),
    [poll, transportError, stopped, turn, pending, clock],
  );

  onScreen.current = turn === null ? null : { question: turn.question, state };

  return {
    state,
    history,
    poll,
    turnId: turn === null || turn.turnId === '' ? null : turn.turnId,
    askedQuestion: turn?.question ?? pending?.question ?? null,
    sending: pending !== null,
    sessionId,
    busy,
    ask,
    stop,
    reset,
  };
}
